/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
import config from 'linced-app/config/environment';
import AssessmentType from 'linced-app/enums/assessment-type';
import EditAssessment from 'linced-app/mixins/edit-assessment';

export default class AssessmentController extends Controller.extend(
  EditAssessment,
) {
  // Services
  @service
  fetch;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  AssessmentType = AssessmentType;

  AssessmentDisplay = AssessmentDisplay;

  Scopes = Scopes;

  @alias('model.assessment')
  assessment;

  @tracked
  editingModule = null;

  @tracked
  moduleLabel = '';

  @action
  setModuleLabel(value) {
    this.moduleLabel = value;
  }

  @tracked
  moduleDescription = '';

  @action
  setModuleDescription(value) {
    this.moduleDescription = value;
  }

  @tracked
  moduleIsModerated = false;

  @tracked
  selectedGradeSchedule = null;

  @tracked
  deleteModuleDialogOpen = false;

  @tracked
  moduleGradesToDelete = [];

  @tracked
  selected = [];

  @tracked
  moduleDialogOpen;

  @tracked
  moduleErrorMessage;

  @tracked
  moduleSaveInProgress;

  // availableSchedules is an array of schedules which are available to the current user.
  @computed(
    'model.gradeschedules.@each.{isShared,authorId,id,isHidden}',
    'editingModule.id',
    'selectedGradeSchedule.id',
    'session.personId',
  )
  get availableSchedules() {
    let personId = this.get('session.personId');
    let selectedId = this.get('selectedGradeSchedule.id');
    let schedules = this.get('model.gradeschedules') || [];

    return schedules.filter((schedule) => {
      return (
        (schedule.get('isShared') ||
          schedule.get('authorId') === personId ||
          schedule.get('id') === selectedId) &&
        !schedule.get('isHidden')
      );
    });
  }

  // clearSelection resets the selected
  clearSelection() {
    this.selected = [];
  }

  @action
  noop() {}

  @action
  toggleSelected(person) {
    if (this.selected.includes(person)) {
      this.selected = this.selected.filter((p) => p !== person);
    } else {
      this.selected = [...this.selected, person];
    }
  }

  @action
  selectAll() {
    if (this.selected.length >= this.assessment.sortedModules.length) {
      // All or more are already selected, so
      // clear selection.
      this.clearSelection();
    } else {
      // Not all selected; select them all.
      this.selected = [...this.assessment.sortedModules];
    }
  }

  @action
  deleteModules() {
    let deletePromises = this.selected.map((module) => {
      // First delete grades associated with the module
      let deleteGrades = Promise.resolve();
      if (this.moduleGradesToDelete.length) {
        let deleteGradesUri = `${config.APP.apiHost}/assessments/v4/modules/${module.id}/grades`;
        deleteGrades = this.fetch.delete(deleteGradesUri).catch((err) => {
          this.toaster.error(
            'An error occurred while deleting linked grades. Please try again.',
          );
          throw err;
        });
      }

      // After grades are deleted, delete the module itself. A successful
      // delete also drops it from assessment.modules.
      return deleteGrades.then(() =>
        module.destroyRecord().catch((err) => {
          // Undelete it so it stays listed.
          rollbackRecord(module);
          this.toaster.error(
            'An error occurred while deleting modules. Please try again.',
          );
          throw err;
        }),
      );
    });

    // Wait for all promises to resolve
    return Promise.all(deletePromises)
      .then(() => {
        this.clearSelection();
        this.deleteModuleDialogOpen = false;
        this.toaster.success(
          'Selected modules have been deleted successfully.',
        );
      })
      .catch(() => {
        // The failing step has already shown its error; keep the dialog open.
      });
  }

  @action
  openDialog() {
    let moduleIds = mapBy(this.selected, 'id');
    return this.store
      .query('assessments/grade', {
        schoolId: this.get('session.school.id'),
        assessmentModuleIds: JSON.stringify(moduleIds),
      })
      .then((response) => {
        this.moduleGradesToDelete = response;
        this.deleteModuleDialogOpen = true;
      });
  }

  @action
  selectGradeSchedule(schedule) {
    this.selectedGradeSchedule = schedule;
  }

  @action
  saveModule() {
    let module = this.editingModule;
    let store = this.store;
    this.moduleSaveInProgress = true;

    // Create a new Module if needed
    if (isEmpty(module)) {
      module = store.createRecord('assessments/module', {
        schoolId: this.get('session.school'),
        assessmentId: this.assessment,
        authorId: this.get('session.person'),
        // Add to the end by default
        sequence: this.get('assessment.modules.length'),
      });
    }

    // Set properties from form
    module.setProperties({
      label: this.moduleLabel,
      description: this.moduleDescription,
      isModerated: this.moduleIsModerated,
      gradeScheduleId: this.selectedGradeSchedule,
    });

    module
      .save()
      .then(() => {
        this.moduleDialogOpen = false;
      })
      .catch((err) => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(module);
        this.moduleErrorMessage = err;
      })
      .then(() => {
        this.moduleSaveInProgress = false;
      });
  }

  @action
  toggleModuleDialog() {
    this.toggleProperty('moduleDialogOpen');
  }

  @action
  async newModule() {
    await this.resetModuleForm();
    this.moduleDialogOpen = true;
  }

  @action
  async editModule(module) {
    await this.resetModuleForm();
    this.editingModule = module;
    this.moduleLabel = module.get('label');
    this.moduleDescription = module.get('description');
    this.moduleIsModerated = module.get('isModerated');
    // A belongsTo takes a record, not the async proxy.
    this.selectedGradeSchedule = await module.gradeSchedule;
    this.moduleDialogOpen = true;
  }

  @action
  moveModule({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      // No change in list or position.
      return;
    }

    // A fragment array's .splice() comes from the fragment-array-mutable
    // initializer (StatefulArray only has .replace() natively).
    const [item] = sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    // Update assessment's module sequences.
    targetList.forEach(function (item, index) {
      item.set('sequence', index);
      if (item.get('hasDirtyAttributes')) {
        item.save();
      }
    });
  }

  async resetModuleForm() {
    this.moduleErrorMessage = '';
    this.moduleLabel = '';
    this.moduleDescription = '';
    this.moduleIsModerated = this.get('assessment.isModerated');
    // A belongsTo takes a record, not the async proxy.
    this.selectedGradeSchedule = await this.assessment?.gradeSchedule;
    this.editingModule = null;
  }
}
